// Single source of truth for the app's supported phone dial-code prefixes.
// Keep in sync with the `Country` seed at
// `packages/database/prisma/seed-catalog.ts`. Values include the leading
// "+" so they can be used directly as the visible prefix in the phone
// field and as the dial-code portion in a "+${prefix}${digits}" string.
export const COUNTRY_DIAL_CODES: Record<string, string> = {
  AR: "+54",
  CL: "+56",
  CO: "+57",
  EC: "+593",
  ES: "+34",
  GT: "+502",
  MX: "+52",
  PA: "+507",
  PE: "+51",
  PY: "+595",
  US: "+1",
  VE: "+58",
};

// Used when the resolved country isn't in the map. Matches the prior
// per-call-site behavior — CO was the implicit default in lead-capture.
export const DEFAULT_DIAL_CODE = "+57";

const DIAL_CODE_TO_COUNTRY: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_DIAL_CODES).map(([country, dial]) => [
    dial.slice(1),
    country,
  ])
);

// Longest-prefix-first, derived from the actual dial codes above instead of
// hardcoded, so adding a code of a new length doesn't silently break matching.
const PREFIX_LENGTHS = [
  ...new Set(Object.keys(DIAL_CODE_TO_COUNTRY).map((code) => code.length)),
].sort((a, b) => b - a);

/**
 * Returns the ISO-3166 alpha-2 country code for a given E.164 phone number
 * using a longest-prefix match on the calling code, limited to the countries
 * the app actually supports (see `COUNTRY_DIAL_CODES`).
 * Returns null if the calling code is not recognized.
 */
export function getCountryFromPhone(phone: string): string | null {
  if (!phone.startsWith("+")) return null;
  const digits = phone.slice(1);
  for (const len of PREFIX_LENGTHS) {
    const country = DIAL_CODE_TO_COUNTRY[digits.slice(0, len)];
    if (country) return country;
  }
  return null;
}
